/**
 * Finalize-with-payment-source — what the mobile client posts to
 * `POST /api/paypal-orders/finalize-with-payment-source` after tokenizing a
 * native wallet payment on-device (PassKit Apple Pay on iOS, Google Pay
 * PaymentsClient on Android).
 *
 * This is the third money channel, sibling of:
 *   - CREATE  (`./checkout-intent.ts` — `CreateOrderRequest`): server authors
 *     `purchase_units` from `intent`; client-supplied `paymentSource` carries
 *     only verification metadata.
 *   - CAPTURE (`./capture-order.ts` — `CaptureOrderRequest`): client posts an
 *     already-approved PayPal order id + opaque `checkoutData`.
 *
 * FINALIZE fuses both: the server resolves `intent` → `purchase_units`
 * (Full A money ownership, same as CREATE), attaches the DEVICE-TOKENIZED
 * wallet payload as `payment_source.{apple_pay|google_pay}` on the PayPal
 * Orders v2 create call, captures in the same request, then persists the
 * Kraahl order from `checkoutData` (same post-money discipline as CAPTURE).
 *
 * Token opacity: `payment_token` / `tokenized_card` are `z.unknown()` on
 * purpose. The encrypted network token is minted by Apple/Google, consumed
 * verbatim by PayPal, and is NEVER inspected, transformed, or logged by our
 * server (see PAYPAL_NATIVE_SDK_PLAN.md — "The endpoint MUST NOT log the raw
 * payment token"). Schema-pinning its internals would couple us to a shape
 * two external vendors own.
 *
 * `checkoutData` stays opaque at the envelope (same rationale as
 * `capture-order.ts` header); the route boundary-parses it against
 * `CheckoutPayloadSchema` when present, mirroring the capture route.
 *
 * @see apps/mobile/lib/api-paypal.ts finalizePayPalOrderWithPaymentSource — producer
 * @see apps/mobile/PAYPAL_NATIVE_SDK_PLAN.md §"Backend prerequisite"
 * @see app/api/paypal-orders/finalize-with-payment-source/route.ts — handler
 */

import { z } from "zod";
import { pinned } from "./pin";
import { CheckoutIntentSchema, type CheckoutIntent } from "./checkout-intent";

// ---------------------------------------------------------------------------
// Request
// ---------------------------------------------------------------------------

/**
 * PassKit-tokenized Apple Pay payload. `attributes.payment_token` is the
 * encrypted `PKPaymentToken`-derived object the iOS bridge resolves with
 * (base64 `paymentData` + `paymentMethod` + `transactionIdentifier`).
 * Opaque by design — forwarded to PayPal, never parsed.
 */
export interface FinalizeOrderApplePaySource {
  attributes: {
    /** Encrypted PassKit payment token. Opaque; never logged. */
    payment_token: unknown;
  };
  /** Buyer name from the PassKit billing contact, when available. */
  name?: string;
  /** Buyer email from the PassKit contact, when available. */
  email_address?: string;
}

/**
 * Google Pay tokenized-card payload. `card.tokenized_card` is the value of
 * `paymentMethodData.tokenizationData.token` from the Google Pay response
 * (JSON-parsed by the mobile client when possible, raw string otherwise).
 */
export interface FinalizeOrderGooglePaySource {
  card: {
    /** Cardholder name from the Google Pay response, when available. */
    name?: string;
    /** Google Pay tokenizationData token. Opaque; never logged. */
    tokenized_card: unknown;
  };
  /**
   * Client-side attributes echo (mobile type carries it optionally). The
   * SERVER IGNORES this field — the verification block is authored
   * server-side (`SCA_WHEN_REQUIRED`, mirroring the web client), so a
   * crafted body cannot downgrade SCA. Accepted at the schema so the
   * mobile producer type stays assignable.
   */
  attributes?: unknown;
}

/**
 * Exactly ONE of `apple_pay` / `google_pay` must be present. Enforced at
 * runtime by `buildFinalizePaymentSource` (`lib/payments/
 * paypal-finalize-payment-source.ts`), not by the schema — mirroring how the
 * CREATE channel splits envelope parse (schema) from payment-source
 * narrowing (`normalizePaymentSource` → 400 INVALID_PAYMENT_SOURCE).
 */
export interface FinalizeOrderPaymentSource {
  apple_pay?: FinalizeOrderApplePaySource;
  google_pay?: FinalizeOrderGooglePaySource;
}

/** Full request body for `POST /api/paypal-orders/finalize-with-payment-source`. */
export interface FinalizeOrderRequest {
  /** Checkout intent — server resolves it to DB-derived money (Full A). */
  intent: CheckoutIntent;
  /** Device-tokenized wallet payload. */
  paymentSource: FinalizeOrderPaymentSource;
  /**
   * Optional checkout metadata for server-side order persistence and
   * reconciliation recovery. Opaque at the contract layer; boundary-parsed
   * against `CheckoutPayloadSchema` by the route when present (422
   * INVALID_CAPTURE_PAYLOAD_SHAPE on mismatch — mirror of the capture route).
   */
  checkoutData?: Record<string, unknown>;
}

export const FinalizeOrderApplePaySourceSchema =
  pinned<FinalizeOrderApplePaySource>()(
    z.object({
      attributes: z.object({ payment_token: z.unknown() }),
      name: z.string().optional(),
      email_address: z.string().optional(),
    }),
  );

export const FinalizeOrderGooglePaySourceSchema =
  pinned<FinalizeOrderGooglePaySource>()(
    z.object({
      card: z.object({
        name: z.string().optional(),
        tokenized_card: z.unknown(),
      }),
      attributes: z.unknown().optional(),
    }),
  );

export const FinalizeOrderPaymentSourceSchema =
  pinned<FinalizeOrderPaymentSource>()(
    z.object({
      apple_pay: FinalizeOrderApplePaySourceSchema.optional(),
      google_pay: FinalizeOrderGooglePaySourceSchema.optional(),
    }),
  );

export const FinalizeOrderRequestSchema = pinned<FinalizeOrderRequest>()(
  z.object({
    intent: CheckoutIntentSchema,
    paymentSource: FinalizeOrderPaymentSourceSchema,
    checkoutData: z.record(z.string(), z.unknown()).optional(),
  }),
);

// ---------------------------------------------------------------------------
// Response — `{ ok: true, ... }` on 2xx, `{ ok: false, ... }` on error
// ---------------------------------------------------------------------------
//
// Unlike the CREATE/CAPTURE envelopes (which predate the `ok` convention and
// can't adopt it without breaking frozen clients), this surface shipped with
// an explicit `ok` discriminator from day one — the mobile plan spec pins it
// (`{ ok: true, orderId, orderResult? }` / `{ ok: false, error, details? }`).

/** Persisted Kraahl order reference (internal id, NOT the PayPal order id). */
export interface FinalizeOrderResult {
  orderId: string;
  orderNumber?: string;
}

export interface FinalizeOrderSuccessResponse {
  ok: true;
  /**
   * PayPal order id — the money-side identifier. Always present on success:
   * money HAS moved by the time this body is emitted.
   */
  orderId: string;
  /**
   * Persisted Kraahl order. ABSENT when persistence failed after a
   * successful capture (`paymentStatus: "captured_order_pending"` — a
   * recovery row was written and reconciliation will create the order), or
   * when no `checkoutData` was posted.
   */
  orderResult?: FinalizeOrderResult;
  /** Carried only on the captured-but-order-pending branch. */
  code?: "ORDER_CREATION_FAILED";
  /** Carried only on the captured-but-order-pending branch. */
  paymentStatus?: "captured_order_pending";
  /** PayPal-Debug-Id passthrough for support tracing. */
  debugId?: string | null;
}

export interface FinalizeOrderErrorResponse {
  ok: false;
  /** Human-readable failure message. */
  error: string;
  /** Machine-readable code from `PAYMENTS_ERROR_CODES`. */
  code: string;
  /** Opaque structured detail (Zod issues, PayPal error body, …). */
  details?: unknown;
  /** Structured PayPal `details[0].issue` when the failure is seller-side. */
  paypalIssue?: string;
  /** PayPal-Debug-Id passthrough. */
  debugId?: string | null;
}

export type FinalizeOrderResponse =
  | FinalizeOrderSuccessResponse
  | FinalizeOrderErrorResponse;

export const FinalizeOrderResultSchema = pinned<FinalizeOrderResult>()(
  z.object({
    orderId: z.string(),
    orderNumber: z.string().optional(),
  }),
);

export const FinalizeOrderSuccessResponseSchema =
  pinned<FinalizeOrderSuccessResponse>()(
    z.object({
      ok: z.literal(true),
      orderId: z.string().min(1),
      orderResult: FinalizeOrderResultSchema.optional(),
      code: z.literal("ORDER_CREATION_FAILED").optional(),
      paymentStatus: z.literal("captured_order_pending").optional(),
      debugId: z.string().nullable().optional(),
    }),
  );

export const FinalizeOrderErrorResponseSchema =
  pinned<FinalizeOrderErrorResponse>()(
    z.object({
      ok: z.literal(false),
      error: z.string(),
      code: z.string(),
      details: z.unknown().optional(),
      paypalIssue: z.string().optional(),
      debugId: z.string().nullable().optional(),
    }),
  );

export const FinalizeOrderResponseSchema = pinned<FinalizeOrderResponse>()(
  z.discriminatedUnion("ok", [
    FinalizeOrderSuccessResponseSchema,
    FinalizeOrderErrorResponseSchema,
  ]),
);
